import { useCallback, useState } from 'react'
import { Layout } from '../components/layout/Layout'
import { UploadDropzone } from '../components/upload/UploadDropzone'
import { UploadProgress } from '../components/upload/UploadProgress'
import { useUpload } from '../hooks/useUpload'
import { useAlbums } from '../hooks/useAlbums'
import { adminApiClient } from '../api/client'
import { Folder, Tags, Upload as UploadIcon, X } from 'lucide-react'

export const Upload = () => {
  const { uploads, addFiles, removeUpload, startUpload, clearCompleted, isUploading } = useUpload()
  const { data: albums } = useAlbums(adminApiClient)
  const [metadata, setMetadata] = useState<{
    albumId: string
    tags: string
  }>({
    albumId: '',
    tags: '',
  })

  const selectedAlbum = albums?.find((a) => a.albumId === metadata.albumId)

  const handleFilesSelected = useCallback((files: FileList | null) => {
    if (files) {
      addFiles(files)
    }
  }, [addFiles])

  const handleStartUpload = useCallback(() => {
    if (!metadata.albumId) return
    uploads.forEach((upload) => {
      if (upload.status === 'pending') {
        startUpload(upload.id, {
          albumId: metadata.albumId,
          tags: metadata.tags.split(',').map((t) => t.trim()).filter(Boolean),
        })
      }
    })
  }, [uploads, metadata, startUpload])

  const pendingCount = uploads.filter(u => u.status === 'pending').length
  const completedCount = uploads.filter(u => u.status === 'completed').length
  const hasPending = pendingCount > 0

  return (
    <Layout
      title="Upload Photos"
      actions={
        uploads.length > 0 && (
          <button
            onClick={() => uploads.filter(u => u.status !== 'uploading').forEach(u => removeUpload(u.id))}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            Clear All
          </button>
        )
      }
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Metadata Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Tags className="w-5 h-5 text-blue-500" />
              Photo Details
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              These settings will apply to all photos you upload
            </p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Album Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center gap-2">
                    <Folder className="w-4 h-4" />
                    Album
                  </span>
                </label>
                <div className="relative">
                  <select
                    value={metadata.albumId}
                    onChange={(e) => setMetadata({ ...metadata, albumId: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                    required
                  >
                    <option value="">Select an album...</option>
                    {albums?.map((album) => (
                      <option key={album.albumId} value={album.albumId}>
                        {album.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {albums?.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    No albums found. <a href="/admin/albums" className="underline">Create an album first</a>.
                  </p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center gap-2">
                    <Tags className="w-4 h-4" />
                    Tags
                  </span>
                </label>
                <input
                  type="text"
                  value={metadata.tags}
                  onChange={(e) => setMetadata({ ...metadata, tags: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="landscape, vacation, 2024"
                />
                <p className="text-xs text-gray-400 mt-1">Separate tags with commas</p>
              </div>
            </div>

            {/* Visibility note */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Visibility
              </label>
              {selectedAlbum ? (
                <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 text-blue-800 text-sm">
                  Photos will inherit the album&rsquo;s visibility (<strong>{selectedAlbum.visibility}</strong>). The album is the sole authority for a photo&rsquo;s reachability — change it from the album&rsquo;s edit page.
                </div>
              ) : (
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-600 text-sm">
                  Select an album to see its visibility. A photo&rsquo;s visibility always matches its album.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upload Dropzone */}
        <UploadDropzone onFilesSelected={handleFilesSelected} />

        {/* Upload Queue */}
        {uploads.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <UploadIcon className="w-5 h-5 text-blue-500" />
                  Upload Queue
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {pendingCount} pending • {completedCount} completed
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                {completedCount > 0 && (
                  <button
                    onClick={clearCompleted}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                  >
                    Clear Completed
                  </button>
                )}
                {hasPending && (
                  <button
                    onClick={handleStartUpload}
                    disabled={isUploading || !metadata.albumId}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {isUploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <UploadIcon className="w-4 h-4" />
                        Start Upload ({pendingCount})
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6">
              <UploadProgress uploads={uploads} onRemove={removeUpload} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}